/**
 * Analyzer: Day Grade
 *
 * Emits the day's composite letter grade as a primary observation so the
 * Coach pipeline carries it like any other signal. Downstream surfaces
 * (Daily Review modal header, future Week rollup) read the letter from the
 * observation's `evidence` block so they stay consistent with the engine.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types';
import { computeGrade } from '../../dayUtils';

export const key = 'dayGrade';
export const tier = 'free';

const SEVERITY_FOR_GRADE = (letter) => {
  if (!letter) return SEVERITY.INFO;
  const head = letter[0];
  if (head === 'A') return SEVERITY.SUCCESS;
  if (head === 'B') return SEVERITY.SUCCESS;
  if (head === 'C') return SEVERITY.INFO;
  if (head === 'D') return SEVERITY.WARNING;
  if (head === 'F') return SEVERITY.CRITICAL;
  return SEVERITY.INFO;
};

// Title omits the letter — it's already shown as a badge in the Coach panel
// header. Repeating it here was the "double-emit" the audit flagged. The
// metric block + evidence still carry the letter for downstream consumers.
const TITLE_FOR_GRADE = (letter) => {
  if (!letter) return 'Day graded';
  const head = letter[0];
  if (head === 'A') return 'Top-tier execution today';
  if (head === 'B') return 'Solid day — one adjustment from an A';
  if (head === 'C') return 'Middling day — see what\'s holding the grade down';
  if (head === 'D') return 'Rough day — review before the next session';
  if (head === 'F') return 'Failed discipline — tonight\'s review counts';
  return 'Day graded';
};

const ACTION_FOR_GRADE = (letter) => {
  if (!letter) return null;
  const head = letter[0];
  if (head === 'A') return 'Write down what went right — A-days are rare and the pattern is teachable.';
  if (head === 'B') return 'One or two adjustments separate a B from an A. Check the weakest domain below.';
  if (head === 'C') return 'Middle-ground day. The observations below point at what\'s holding you at C.';
  if (head === 'D') return 'Step back. Review mistakes + time-of-day breakdown before the next session.';
  if (head === 'F') return 'Close the platform. Tomorrow\'s edge starts with tonight\'s honest review.';
  return null;
};

export const analyze = (ctx) => {
  const { day } = ctx;
  if (!day || !day.core) return null;

  // Live-trade gate (v7.5.14 contract extended to day-scope): when the
  // day's only trades are still OPEN, the closed-only core stats are all
  // zeros and the resulting grade would be "Rough day — review before the
  // next session" with 0% WR + 0R + PF 0 + $-N. That observation fires
  // WHILE the user is still in the trade — exactly the panic-inducing
  // bad-feedback v7.5.14 was created to prevent. Suppress until at least
  // one trade has closed today.
  if (day.core.closedTotal === 0) return null;

  const grade = computeGrade(day);
  const { winRate, profitFactor, avgR, totalPnL } = day.core;

  const winRatePct = (winRate * 100).toFixed(0);
  const pfStr = profitFactor === Infinity ? '∞' : profitFactor.toFixed(2);
  const pnlStr = `${totalPnL >= 0 ? '+' : ''}${Math.round(totalPnL)}`;

  return makeObservation({
    id: `${key}:${day.date}`,
    analyzer: key,
    scope: 'day',
    scopeId: day.date,
    severity: SEVERITY_FOR_GRADE(grade.letter),
    category: CATEGORY.PROCESS,
    title: TITLE_FOR_GRADE(grade.letter),
    detail: `${winRatePct}% WR · ${avgR.toFixed(2)}R avg · PF ${pfStr} · $${pnlStr}.`,
    action: ACTION_FOR_GRADE(grade.letter),
    evidence: grade,
    metric: { label: 'Grade', value: grade.letter, baseline: grade.score, unit: 'score' },
    confidence: 0.9,
    priority: 70
  });
};
